import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "@/components/web/Seo";
import { MaskedLines, FadeUp } from "@/components/web/Reveal";

const STEPS = [
  {
    n: "01",
    title: "Submit a link",
    body: "Paste any URL into the LinkExpln scanner — a message you received, a checkout page, a login prompt, a shortened link. Anything you're not sure about.",
    detail: "Supports full URLs, shortened links and redirect chains.",
  },
  {
    n: "02",
    title: "AI takes it apart",
    body: "Our analysis engine inspects the link's anatomy: domain reputation and age, lookalike characters, SSL certificate posture, redirect behavior and known threat-intelligence signals.",
    detail: "Dozens of signals evaluated in parallel, in seconds.",
  },
  {
    n: "03",
    title: "Get a clear verdict",
    body: "No jargon. You get a plain-language answer — safe, suspicious, or dangerous — together with the specific reasons behind the verdict so you can decide with confidence.",
    detail: "Human-readable explanations, not raw scores.",
  },
  {
    n: "04",
    title: "Stay protected",
    body: "Found something malicious? Report it in one click. Every report strengthens the shield for the whole community and helps flag emerging scam campaigns faster.",
    detail: "Community reporting feeds continuous protection.",
  },
];

export default function HowItWorks() {
  return (
    <>
      <Seo
        title="How It Works — LinkExpln"
        description="How LinkExpln scans URLs: submit a link, our AI analyzes domain reputation, redirects and phishing signals, and you get a clear safety verdict in seconds."
      />

      <section className="pt-24 pb-16 sm:pt-32 sm:pb-20 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="ls-overline mb-6">The Process</p>
          <h1
            data-testid="how-it-works-heading"
            className="font-heading font-black tracking-tighter text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.95]"
          >
            <MaskedLines lines={["FROM LINK", "TO VERDICT."]} />
          </h1>
          <p className="mt-8 max-w-xl text-zinc-400 text-base sm:text-lg leading-relaxed">
            Four steps between uncertainty and confidence. Here is exactly what
            happens when you hand a URL to LinkExpln.
          </p>
        </div>
      </section>

      <section data-testid="steps-section" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 space-y-28">
          {STEPS.map((s, i) => (
            <FadeUp key={s.n}>
              <div className={`grid grid-cols-1 md:grid-cols-12 gap-8 items-start ${i % 2 === 1 ? "" : ""}`}>
                <div className={`md:col-span-4 ${i % 2 === 1 ? "md:order-2 md:text-right" : ""}`}>
                  <span className="font-mono-ls font-light text-8xl sm:text-9xl leading-none text-transparent" style={{ WebkitTextStroke: "1px rgba(204,255,0,0.35)" }}>
                    {s.n}
                  </span>
                </div>
                <div className={`md:col-span-7 ${i % 2 === 1 ? "md:order-1 md:col-start-1" : "md:col-start-6"}`}>
                  <h2 className="font-heading font-light text-3xl sm:text-4xl text-white tracking-tight">{s.title}</h2>
                  <p className="mt-5 text-zinc-400 leading-relaxed max-w-lg">{s.body}</p>
                  <p className="mt-5 font-mono-ls text-[11px] tracking-[0.2em] uppercase text-[#00C5FE]">{s.detail}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <h2 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-white text-balance max-w-2xl">
              Ready to see it in action?
            </h2>
            <Link
              to="/scanner"
              data-testid="how-cta-scanner"
              className="mt-8 inline-flex items-center gap-2 btn-brand text-black font-heading font-bold px-8 py-4 transition-colors duration-200"
            >
              Try the Scanner <ArrowRight size={18} strokeWidth={2.5} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
